import { translate } from 'secullum-i18n';
import { formatDate } from 'secullum-react-native-ui';
import { Container } from 'unstated';
import { getTheme } from '../../shared/modules/layout';
import Api from '../modules/api';
import {
  AssinaturaDigitalCartaoPontoDados,
  AssinaturaDigitalCartaoPontoStatus,
  AssinaturaDigitalCartaoPontoFiltrosStatus,
  AssinaturaDigitalCartaoPontoFiltros,
  TipoPerfil
} from '../modules/types';

export interface State {
  lista: Array<AssinaturaDigitalCartaoPontoDados>;
  precisaProcessarAssinaturasDigitaisCartaoPonto?: boolean;
  erro: string;
  erroConexao: boolean;
  onTentarNovamente: (() => void) | null;
}

const theme = getTheme();

export const getIconeStatus = (status: AssinaturaDigitalCartaoPontoStatus) => {
  switch (status) {
    case AssinaturaDigitalCartaoPontoStatus.Aprovado:
      return 'thumbs-up';
    case AssinaturaDigitalCartaoPontoStatus.Rejeitado:
      return 'thumbs-down';
    case AssinaturaDigitalCartaoPontoStatus.NaoRespondido:
      return 'hourglass-half';
    default:
      // Assinaturas que estão "AguardandoAprovacaoGerentes" ainda não estão disponíveis para o funcionário.
      // Nesse caso, como não está aguardando nada do funcionário, deixamos o espaço do ícone em branco.
      return '';
  }
};

export const getCorStatus = (status: AssinaturaDigitalCartaoPontoStatus) => {
  switch (status) {
    case AssinaturaDigitalCartaoPontoStatus.Aprovado:
      return theme.successColor;
    case AssinaturaDigitalCartaoPontoStatus.Rejeitado:
      return theme.errorColor;
    case AssinaturaDigitalCartaoPontoStatus.NaoRespondido:
      return theme.warningColor;
    default:
      // Assinaturas que estão "AguardandoAprovacaoGerentes" ainda não estão disponíveis para o funcionário.
      // Nesse caso, como não está aguardando nada do funcionário, deixamos o espaço do ícone em branco.
      return '';
  }
};

export const getPeriodoFormatado = (
  dados: AssinaturaDigitalCartaoPontoDados,
  format = 'dd/MM/yy'
) => {
  return `${formatDate(dados.dataInicio, format)} - ${formatDate(
    dados.dataFim,
    format
  )}`;
};

export class AssinaturaDigitalCartaoPontoContainer extends Container<State> {
  state: State = {
    lista: [],
    erro: '',
    erroConexao: false,
    onTentarNovamente: null
  };

  getItensStatusAssinaturaDigitalCartaoPonto() {
    return [
      {
        nativeID: 'assinatura-digital-cartao-ponto-filtros-todas',
        value: AssinaturaDigitalCartaoPontoFiltrosStatus.Todos,
        label: translate('Todas')
      },
      {
        nativeID: 'assinatura-digital-cartao-ponto-filtros-processadas',
        value: AssinaturaDigitalCartaoPontoFiltrosStatus.Processados,
        label: translate('Aceitas') + '/' + translate('Rejeitadas')
      },
      {
        nativeID: 'assinatura-digital-cartao-ponto-filtros-pendentes',
        value: AssinaturaDigitalCartaoPontoFiltrosStatus.Pendentes,
        label: translate('Aguardando Retorno')
      }
    ];
  }

  getItensStatusAssinaturaDigitalCartaoPontoGerente() {
    return [
      {
        nativeID: 'assinatura-digital-cartao-ponto-filtros-todas',
        value: AssinaturaDigitalCartaoPontoFiltrosStatus.Todos,
        label: translate('Todas')
      },
      {
        nativeID:
          'assinatura-digital-cartao-ponto-filtros-aceitas-rejeitadas-gerente',
        value: AssinaturaDigitalCartaoPontoFiltrosStatus.ProcessadosGerentes,
        label: translate('Aceitas') + '/' + translate('Rejeitadas')
      },
      {
        nativeID: 'assinatura-digital-cartao-ponto-filtros-pendentes-gerente',
        value:
          AssinaturaDigitalCartaoPontoFiltrosStatus.AguardandoAprovacaoGerentes,
        label: translate('Aguardando Aprovação')
      }
    ];
  }

  loadAsync = async (filtros: AssinaturaDigitalCartaoPontoFiltros) => {
    const api = new Api();

    await api.getAssinaturasDigitaisCartaoPonto(filtros, {
      onSuccess: async (lista: Array<AssinaturaDigitalCartaoPontoDados>) => {
        this.setState({
          lista,
          precisaProcessarAssinaturasDigitaisCartaoPonto: lista.some(
            assinatura => {
              const estado =
                filtros.perfil === TipoPerfil.Gerente
                  ? assinatura.estadoGerente
                  : assinatura.estado;

              return (
                estado === AssinaturaDigitalCartaoPontoStatus.NaoRespondido
              );
            }
          )
        });
      },
      onError: (err: Error) => {
        this.setState({
          erro: err.message,
          erroConexao: err.name === 'TimeoutError',
          onTentarNovamente: () => this.loadAsync(filtros)
        });
      }
    });
  };

  carregarAsync = async (
    assinaturaId: number,
    gerente: boolean = false
  ): Promise<AssinaturaDigitalCartaoPontoDados | null> => {
    const api = new Api();

    let assinaturaCarregada: AssinaturaDigitalCartaoPontoDados | null = null;

    await api.getAssinaturaDigitalCartaoPonto(assinaturaId, {
      onSuccess: async (assinatura: AssinaturaDigitalCartaoPontoDados) => {
        assinaturaCarregada = assinatura;

        // A assinatura pode não existir mais (ex: foi excluída após a notificação ser gerada).
        // Nesses casos garantimos que a lista fique vazia, e não como null, prevenindo que
        // algum trecho tente acessar o primeiro item assumindo que sempre haverá uma assinatura válida.
        const lista = assinatura ? [assinatura] : [];

        this.setState({
          lista,
          precisaProcessarAssinaturasDigitaisCartaoPonto: lista.some(
            assinatura => {
              const estado = gerente
                ? assinatura.estadoGerente
                : assinatura.estado;

              return (
                estado === AssinaturaDigitalCartaoPontoStatus.NaoRespondido
              );
            }
          )
        });
      },
      onError: (err: Error) => {
        this.setState({
          erro: err.message,
          erroConexao: err.name === 'TimeoutError',
          onTentarNovamente: () => this.carregarAsync(assinaturaId, gerente)
        });
      }
    });

    return assinaturaCarregada;
  };

  limparErro = () => {
    this.setState({
      erro: '',
      erroConexao: false,
      onTentarNovamente: null
    });
  };

  informarErro = (
    erro: string,
    erroConexao: boolean = false,
    onTentarNovamente: (() => void) | null = null
  ) => {
    this.setState({
      erro,
      erroConexao,
      onTentarNovamente
    });
  };
}

export default new AssinaturaDigitalCartaoPontoContainer();
